import type { IsoTimestamp } from './common.js';
import type { ContactStatus, LeadQuality, ServiceFlag } from './lead.js';
import type { LeadOutcome } from './leadOutcome.js';
import type { LeadListRow } from './leadList.js';
import type { LeadHistoryPage } from '../lib/speedToLead/leadHistory.js';
import type { ContactAction } from '../lib/leads/contactState.js';

// THE SHARED LEADS SPLIT VIEW (KyckConnect redesign Phase 2, Part B): the wire shapes the staff pages and the club portal share.
// ⚠️ Built field by field on the server, never a spread of the stored doc. The LIST carries the last four phone digits only; the
// full number travels ONLY in the detail response, and only to staff and to the club's own portal users (never in a URL, never
// in a log). Nothing Kyck-only (bot instructions, leases, models, attempts, internal reasons) is on either shape.

/** The stage tabs, in order. They are NOT mutually exclusive: "Needs your team" overlaps the others. */
export const LEAD_STAGE_TABS = ['all', 'needs_team', 'not_contacted', 'in_conversation', 'booked', 'joined', 'not_interested'] as const;
export type LeadStageTab = (typeof LEAD_STAGE_TABS)[number];
export const LEAD_STAGE_LABELS: Record<LeadStageTab, string> = {
  all: 'All',
  needs_team: 'Needs you',
  not_contacted: 'New',
  in_conversation: 'Talking',
  booked: 'Booked',
  joined: 'Joined',
  not_interested: 'Not interested',
};
export type LeadStageCounts = Record<LeadStageTab, number>;

/** Who reached the lead first: AI SMS, a call, or nobody yet. */
export const REACHED_BY = ['sam', 'call', 'none'] as const;
export type ReachedBy = (typeof REACHED_BY)[number];

export interface LeadSplitFilters {
  /** Name, phone digits, or email. */
  q?: string;
  receivedFrom?: IsoTimestamp;
  receivedTo?: IsoTimestamp;
  quality?: LeadQuality[];
  source?: string;
  reachedBy?: ReachedBy;
  /** The stage tab. It filters the LIST; the counts ignore it (so every tab shows what it would hold). */
  stage?: LeadStageTab;
  /** Staff only. Test leads are excluded unless this is true. */
  includeTest?: boolean;
  /** Staff only (the all-clients page): narrow to the clubs under this brand / owner group. The portal schema refuses both. */
  brandId?: string;
  groupId?: string;
}

/** One club for the staff all-clients page, with the brand and group it sits under (resolved on the server). */
export interface StaffLeadClub {
  id: string;
  name: string;
  brandId: string | null;
  brandName: string | null;
  groupId: string | null;
  groupName: string | null;
  /** The DEMO club (KyckConnect demo data): listed so staff can open it, shown with a Demo badge, and left out of every rollup. */
  demo: boolean;
}

export interface LeadLatestActivity {
  kind: 'message' | 'call' | 'enquiry';
  /** One short line: the last message ("Mia: …"), or the call result, or "Enquiry received". Never logged. */
  text: string;
  at: IsoTimestamp;
}

/** The two list-row notices the old conversations page showed besides distress. A staff takeover where the lead has written since, and a conversation that hit its reply limit. */
export type LeadRowNotice = 'lead_waiting' | 'reply_cap';
export const LEAD_ROW_NOTICE_LABELS: Record<LeadRowNotice, string> = { lead_waiting: 'Lead waiting', reply_cap: 'Reply cap reached' };

export interface LeadSplitRow extends LeadListRow {
  initials: string;
  latestActivity: LeadLatestActivity | null;
  /** "Call 0:13" / "AI SMS 0:04": the first response's channel and how fast. Null when nobody has responded. */
  firstResponseLabel: string | null;
  /** Possible distress was flagged on this lead's newest conversation. Sorts to the top of the page and is marked in the list. */
  distress: boolean;
  /** "Lead waiting" / "Reply cap reached", from the lead's newest conversation (the old page's attention reasons). Null when neither applies. */
  notice: LeadRowNotice | null;
  /** The first name of the team member who took the newest call, for "Spoke with Matt". Null when no call was taken. */
  staffFirstName: string | null;
  /** The booked appointment (only read for a lead whose outcome is booked), for "Tour booked by text · Sat 31 Oct, 3:30 pm". */
  booking: { startsAt: IsoTimestamp; timezone: string } | null;
  /** The club has neither AI SMS nor calls: the line says the lead went to the CRM. */
  relayOnly: boolean;
}

export interface LeadSplitPage {
  leads: LeadSplitRow[];
  /** Null on a "load more" page (the counts were sent with the first page). */
  counts: LeadStageCounts | null;
  nextCursor: string | null;
  /** The scan stopped at its cap: the counts and list are a floor, and the page says so. */
  scanCapped: boolean;
  /** The distinct sources in the filtered set, for the source filter. Empty on a "load more" page. */
  sources: string[];
}

/** What the club must see before anything else on a lead (the old portal conversation panel's banners and booking). Codes and labels only. */
export interface LeadNotices {
  /** Possible distress was flagged on any of this lead's conversations at the club. */
  distress: boolean;
  /** The assistant handed the newest conversation to the team and no staff member has taken over. Friendly reasons, never internal detail. */
  handoff: { reasons: string[] } | null;
  /** The number opted out of texts (a STOP), so nothing can be sent. */
  optedOut: boolean;
  /** The booked appointment, if any: the instant and the club's timezone, so each look can word it the same way. */
  appointment: { startsAt: IsoTimestamp; timezone: string } | null;
}

export interface LeadSplitDetail {
  id: string;
  accountId: string;
  accountName: string | null;
  assistantName: string | null;
  name: string | null;
  /** The FULL number: staff and the club's own portal users only. */
  phone: string;
  /** DEMO DATA: a seeded lead. Its number is shown as plain text, never a `tel:` link (so a demo click can never dial). Absent on a real lead. */
  demo?: boolean;
  email: string | null;
  source: string | null;
  campaign: string | null;
  receivedAt: IsoTimestamp;
  /** The first contact attempt: seconds from received to the lead being dialled (or texted). */
  dialledAfterSeconds: number | null;
  firstResponseVia: 'sam' | 'call' | null;
  quality: LeadQuality;
  contactStatus: ContactStatus;
  /** The club only has its leads sent to its CRM (no AI SMS, no Speed to Lead): the contact line says so. */
  relayOnly: boolean;
  outcome: LeadOutcome;
  outcomeSetBy: 'person' | 'ai' | 'system' | null;
  /** Where an AI-set outcome came from, and how sure it was. Null when a person set it or nothing has. */
  outcomeSuggestion: { from: 'call' | 'conversation'; confidence: string | null } | null;
  needsPerson: boolean;
  isTest: boolean;
  services: Record<ServiceFlag, boolean>;
  profile: { goal: string; motivation: string; preferredTimes: string; objections: string };
  /** Text drafts waiting for approval. The staff host links to Approvals; the portal says "Waiting for Kyck to approve". */
  pendingDraftCount: number;
  notices: LeadNotices;
  /** When the outcome was last set, who confirmed it (a first name; "Kyck" in the club's view) and what it came from. For the "Where it's at" sentence. */
  outcomeSetAt: IsoTimestamp | null;
  outcomeConfirmedBy: string | null;
  outcomeSource: 'call' | 'conversation' | null;
  /** The club's timezone, so a time reads in the club's own clock. */
  timezone: string | null;
  /** The AI SMS strip. `available` = the club has AI SMS; `paused` = a person has the texts (Sam is not drafting or sending for this lead). */
  sam: LeadSamState;
  /**
   * The Call and Text buttons. `call` / `text` are null when the club does not have the service (no button at all); otherwise the button is
   * shown and `enabled` / `message` say whether it can be pressed and, if not, why. A DISPLAY rule only: the server refuses again at the action.
   * `canCall` / `canText` mean "the button is shown AND enabled" (what a plain check wants).
   */
  contact: { canCall: boolean; canText: boolean; call: ContactAction | null; text: ContactAction | null };
  history: LeadHistoryPage;
}

/** The "Which phone should we ring first?" content. Names only: no number leaves the server. */
export interface LeadCallOptions {
  staff: Array<{ teamMemberId: string; name: string }>;
  defaultTeamMemberId: string | null;
  /** Why a call cannot be started right now (the server's own sentence), or null. */
  unavailable: { code: string; message: string } | null;
}

/** Sam's state for ONE lead. `pausedByName` is a first name ("Kyck" in the club's view when Kyck staff paused it). */
export interface LeadSamState {
  available: boolean;
  paused: boolean;
  /** A conversation exists for this lead at this club (the switch has something to switch). */
  hasConversation: boolean;
  pausedByName: string | null;
  pausedAt: IsoTimestamp | null;
}
